import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, deviceId, errMsg } from "@/lib/api";

const Ctx = createContext(null);

export function RadioProvider({ children }) {
  const audio = useRef(null);
  const skew = useRef(0);
  const [state, setState] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const playingRef = useRef(false);
  const currentId = useRef(null);
  const [listenStart, setListenStart] = useState(null);

  if (!audio.current && typeof Audio !== "undefined") {
    audio.current = new Audio();
    audio.current.preload = "auto";
  }

  const offsetOf = (cur) => Math.max(0, (Date.now() + skew.current - new Date(cur.start).getTime()) / 1000);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/radio/now", { params: { device_id: deviceId() } });
      skew.current = new Date(data.server_time).getTime() - Date.now();
      setState(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const startAudio = useCallback((cur) => {
    const a = audio.current;
    if (!a || !cur) return;
    const src = cur.audio.startsWith("/api") ? process.env.REACT_APP_BACKEND_URL + cur.audio : cur.audio;
    currentId.current = cur.play_id;
    setListenStart({ play: cur.play_id, at: Date.now() });
    api.post("/radio/heartbeat", { device_id: deviceId(), playing: true }).catch(() => {});
    const seek = () => {
      try { a.currentTime = offsetOf(cur); } catch { /* ignore */ }
    };
    if (a.src !== src) {
      a.src = src;
      a.addEventListener("loadedmetadata", seek, { once: true });
    } else seek();
    a.play().catch(() => { setPlaying(false); playingRef.current = false; });
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const cur = state?.current;
    if (!cur) return;
    if (playingRef.current && cur.play_id !== currentId.current) startAudio(cur);
    const ms = new Date(cur.end).getTime() - (Date.now() + skew.current);
    const t = setTimeout(load, Math.max(500, ms + 400));
    return () => clearTimeout(t);
  }, [state?.current?.play_id, startAudio, load]); // eslint-disable-line

  useEffect(() => { if (audio.current) audio.current.volume = volume; }, [volume]);

  const beat = useCallback(() => api.post("/radio/heartbeat", { device_id: deviceId(), playing: playingRef.current }).catch(() => {}), []);
  useEffect(() => {
    const id = setInterval(beat, 15000);
    return () => clearInterval(id);
  }, [beat]);

  const toggle = async () => {
    if (playingRef.current) {
      audio.current.pause();
      setListenStart(null);
      currentId.current = null;
      playingRef.current = false;
      setPlaying(false);
      beat();
      return;
    }
    playingRef.current = true;
    setPlaying(true);
    const data = state?.current ? state : await load();
    if (data?.current) startAudio(data.current);
    beat();
  };

  const patchCurrent = (patch) => setState((s) => (s ? { ...s, current: { ...s.current, ...patch } } : s));

  const flame = async () => {
    const cur = state?.current;
    if (!cur || cur.my_flamed) return false;
    try {
      const { data } = await api.post("/radio/flame", { device_id: deviceId(), play_id: cur.play_id });
      patchCurrent(data);
      if (cur.hidden) toast.success(`Revealed: ${data.artist?.stage_name}`, { description: "Your blind Flame counts on the Blind Chart." });
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  const react = async (tag) => {
    const cur = state?.current;
    if (!cur || cur.my_reactions.includes(tag)) return;
    patchCurrent({ my_reactions: [...cur.my_reactions, tag], reactions: { ...cur.reactions, [tag]: (cur.reactions[tag] || 0) + 1 } });
    await api.post("/radio/react", { device_id: deviceId(), play_id: cur.play_id, tag }).catch((e) => toast.error(errMsg(e)));
  };

  return (
    <Ctx.Provider value={{ state, playing, toggle, flame, react, volume, setVolume, reload: load, listenStart }}>{children}</Ctx.Provider>
  );
}

export const useRadio = () => useContext(Ctx);
